import { useDrag } from '@use-gesture/react';
import { useState } from 'react';
import { animated, to as interpolate, useSprings } from 'react-spring';
import styled from 'styled-components';

import { Horizontal } from 'atoms/layout/flex';
import { useIsLaptop } from 'hooks/device/useIsLaptop';

const Container = styled(Horizontal)`
  display: flex;
  width: 100%;
  height: 100%;
  cursor: pointer;
  position: relative;
  align-items: center;
  justify-content: center;
`;

const Card = styled(animated.div)<{ width: number }>`
  flex: 1;
  cursor: grab;
  display: flex;
  touch-action: none;
  position: absolute;
  align-items: center;
  will-change: transform;
  justify-content: center;
  background-size: contain;
  background-repeat: no-repeat;
  aspect-ratio: var(--card-aspect-ratio);
  width: ${({ width }) => width}px;

  &:active {
    cursor: grabbing;
  }
`;

const to = (i: number) => ({
  x: 0,
  y: i * -4,
  scale: 1,
  rotation: -10 + Math.random() * 20,
  delay: i * 100,
});
const from = () => ({ x: 0, rotation: 0, scale: 1.5, y: -1000 });

const transform = (r: number, s: number) => `rotateZ(${r}deg) scale(${s})`;

type Props = {
  cards: string[];
  // Enable restack of cards after all have been swiped
  restack?: boolean;
  cardWidth?: number;
};

export const InteractiveStackedCards = ({
  cards,
  restack,
  cardWidth = 240,
}: Props) => {
  const [gone] = useState(() => new Set());
  const [props, api] = useSprings(cards.length, i => ({
    ...to(i),
    from: from(),
  }));
  const isLaptopAndAbove = useIsLaptop();

  const bind = useDrag(
    ({ args: [index], down, movement: [mx], direction: [xDir], velocity }) => {
      // If velocity is reached, trigger the card to be removed
      const trigger = isLaptopAndAbove || velocity[0] > 0.2;

      let direction;
      if (xDir === 0 && isLaptopAndAbove) {
        direction = Math.random() < 0.5 ? -1 : 1;
      } else {
        direction = xDir < 0 ? -1 : 1;
      }

      if (!down && trigger) {
        gone.add(index);
      }

      api.start(i => {
        let x = 0;
        let scale = 1;
        let tension = 500;
        if (index !== i) return {};

        const isGone = gone.has(index);

        if (isGone) {
          tension = 200;
          x = (200 + window.innerWidth) * direction;
        } else if (down) {
          x = mx;
          scale = 1.1;
          tension = 800;
        }
        const rotation = mx / 100 + (isGone ? direction * 5 * velocity[0] : 0);

        return {
          x,
          scale,
          rotation,
          config: {
            tension,
            friction: 50,
          },
        };
      });

      // If all cards have been swiped, restack them
      if (restack && !down && gone.size === cards.length)
        setTimeout(() => {
          gone.clear();
          api.start(i => to(i));
        }, 600);
    }
  );

  return (
    <Container>
      {props.map(({ x, y, rotation, scale }, i) => (
        <Card
          // eslint-disable-next-line react/no-array-index-key
          key={i}
          {...bind(i)}
          width={cardWidth}
          style={{
            x,
            y,
            // Reverse the order to make the first card of the list at the top of the stack
            backgroundImage: `url(${[...cards].reverse()[i]})`,
            transform: interpolate([rotation, scale], transform),
          }}
        />
      ))}
    </Container>
  );
};
